import * as React from 'react';
import * as styles from "./ViewPanel.css";
import { inject, observer } from 'mobx-react';
import { MessagesStore } from '../stores';
import { IAPanel } from '../../panels/Panel';
import DateTimeService from '../../../services/DateTimeService';
import { LocalizationService } from '../../../services/LocalizationService';
import { Helper } from '../../../Helper';
import { ConditionalOutputFactory } from '../../../services/ConditionalOutputFactory';
import { Environment } from '../../../services/Environment';
import * as _html2canvas from "html2canvas";
import jsPDF, { jsPDFOptions } from 'jspdf';
import { IAViewer } from '../../viewer/Viewer';
import { IComposerImage } from '../../../interfaces/IComposerImage';
import { LoggingService } from '../../../services/LoggingService';
import MessageService from '../../../services/MessageService';
import { ViewPanelRecurrenceEditPopup } from './viewPanelRecurrenceEditPopup/ViewPanelRecurrenceEditPopup';

export interface IViewPanelProps {
  id: number;
  messagesStore?: MessagesStore;
  isContentCreator?: boolean;
  dataAutomationIdPrefix?: string;
  top?: number;
  enablePrint?: boolean; // overrides instance setting
  disablePaging?: boolean;
  openingFromLink?: boolean;
  onClose?: () => void;
  onEdit?: () => void;
  getNextPage?: () => Promise<void>;
}

export interface IViewPanelState {
  printing?: boolean;
  printProgressPercentage?: number;
  coverVideoElement?: Element;
  translateToLanguage: {
    itemId: string;
    language: string;
  };
  imageViewerImages: IComposerImage[];
  imageViewerIndex: number;
  showRecurrenceEditPopup: boolean;
}


@inject('messagesStore')
@observer
export class ViewPanel extends React.Component<IViewPanelProps, IViewPanelState> {
  private static readonly PRINT_SCALE = 3;
  private readonly localizationService = new LocalizationService();
  private readonly dateTimeService: DateTimeService = new DateTimeService();
  private panelId: string;

  constructor(props: IViewPanelProps) {
    super(props);
    this.state = {
      printing: false,
      printProgressPercentage: undefined,
      translateToLanguage: undefined,
      imageViewerImages: undefined,
      imageViewerIndex: 0,
      showRecurrenceEditPopup: false
    };
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
    this.panelId = Helper.getRandomStringKey();
  }

  public componentWillReceiveProps(props: IViewPanelProps): void {
    if (props.id != undefined && props.id !== this.props.id) {
      this.props.messagesStore.getInstance();
      // Fix for mobile browser z-index issue
      if (this.props.messagesStore.isMobile && document.getElementById("IA_GlobalNavigator")) {
        const navigatorElement = document.getElementById("IA_GlobalNavigator");
        if (navigatorElement) {
          navigatorElement.style.display = "none";
        }
      }
      //
      if (LoggingService !== undefined) {
        LoggingService.trackEvent(this.props.messagesStore.component === "Messages" ? "OpenMessage" : "OpenEvent", {
          ActionType: this.props.messagesStore.component === "Messages" ? "OpenMessage" : "OpenEvent",
          Component: this.props.messagesStore.component,
          Id: this.props.messagesStore.messageToOpen.id,
          Title: this.props.messagesStore.messageToOpen.title,
          Categories: this.props.messagesStore.messageToOpen.categories,
          StartDate: this.props.messagesStore.messageToOpen.startDate,
          Created: this.props.messagesStore.messageToOpen.created,
          Author: this.props.messagesStore.messageToOpen.author,
          Tenant: this.props.messagesStore.tenant,
          Instance: this.props.messagesStore.instance?.instanceName,
          UserId: this.props.messagesStore.token,
          Environment: this.props.messagesStore.environment
        });
      }
    }
  }

  private emailLink(): void {
    const url = window.location.href.split('?')[0];
    let linkToMessage: string;
    if (this.props.messagesStore.component === "Messages") {
      if (this.props.messagesStore.instance?.settings?.openMessageLink) {
        linkToMessage = `${Environment.getEnvironmentForLinkPortal(this.props.messagesStore.environment)}/${this.props.messagesStore.tenant}/${this.props.messagesStore.instance.instanceName}/${this.props.messagesStore.messageToOpen.id}`;
      } else {
        linkToMessage = `${url}?instance=${this.props.messagesStore.instance.instanceName}&messageId=${this.props.messagesStore.messageToOpen.id}`;
      }
    } else {
      linkToMessage = `${url}?instance=${this.props.messagesStore.instance.instanceName}&eventId=${this.props.messagesStore.messageToOpen.id}`;
    }
    var link = "mailto:"
      + "?subject=" + encodeURIComponent(this.props.messagesStore.messageToOpen.title)
      + "&body=" + encodeURIComponent(linkToMessage);
    window.location.href = link;
  }

  private enableNext(): boolean {
    if (this.props.messagesStore.messageToOpen) {
      if (this.props.messagesStore.messagesOpenedStack.length > 0) {
        return false;
      }
      let disabled = (
        Math.ceil(this.props.messagesStore.messages.length / this.props.messagesStore.numMessages) === this.props.messagesStore.pageNumber &&
        this.props.messagesStore.messages.length <= this.props.messagesStore.messageIndex);
      disabled = this.props.disablePaging;
      const hasNoPages = this.props.messagesStore.pagingInfo === "" || (this.props.messagesStore.onlyShowPinnedMessages && this.props.messagesStore.pagingInfo === undefined);
      const isLastMessage = this.props.messagesStore.messageIndex === this.props.messagesStore.messages?.length - 1;
      if (hasNoPages && isLastMessage) {
        disabled = true;
      }
      return !disabled;
    }
  }

  private nextMessage(): void {
    this.props.messagesStore.pageNumber = Math.ceil((this.props.messagesStore.messageIndex + 1) / this.props.messagesStore.numMessages);
    if (this.props.messagesStore.pageNumber < 1) {
      this.props.messagesStore.pageNumber = 1;
    }
    const nextMessageIsOnPage = Math.ceil((this.props.messagesStore.messageIndex + 2) / this.props.messagesStore.numMessages);
    if (nextMessageIsOnPage !== this.props.messagesStore.pageNumber) {
      if (this.props.messagesStore.pageNumber * this.props.messagesStore.numMessages < this.props.messagesStore.messages.length) {
        this.props.messagesStore.pageNumber++;
        this.props.messagesStore.openNextMessage();
      } else {
        this.props.messagesStore.pageNumber++;
        this.props.getNextPage();
      }
    } else {
      this.props.messagesStore.openNextMessage();
    }
  }

  private enablePrevious(): boolean {
    if (this.props.messagesStore.messagesOpenedStack.length > 0) {
      return true;
    }
    if (this.props.messagesStore.messageToOpen) {
      return this.props.messagesStore.messageIndex > 0 && !this.props.disablePaging;
    }
  }

  private previousMessage(): void {
    if (this.props.messagesStore.messagesOpenedStack.length > 0) {
      this.openPreviouslyOpenedMessage();
      return;
    }
    this.props.messagesStore.pageNumber = Math.ceil((this.props.messagesStore.messageIndex + 1) / this.props.messagesStore.numMessages);
    if (this.props.messagesStore.pageNumber < 1) {
      this.props.messagesStore.pageNumber = 1;
    }
    const previousMessageIsOnPage = Math.ceil((this.props.messagesStore.messageIndex) / this.props.messagesStore.numMessages);
    if (previousMessageIsOnPage !== this.props.messagesStore.pageNumber) {
      if (this.props.messagesStore.pageNumber != 1) {
        this.props.messagesStore.pageNumber--;
        this.props.messagesStore.openPreviousMessage()
      }
    } else {
      this.props.messagesStore.openPreviousMessage()
    }
  }

  private openPreviouslyOpenedMessage() {
    // This is triggered if a message is opened from another message - in this case we open the previous message
    this.props.messagesStore.panelContentIsLoading = true;
    setTimeout(() => {
      this.props.messagesStore.messageToOpen = this.props.messagesStore.messagesOpenedStack.pop();
      this.props.messagesStore.panelContentIsLoading = false;
    }, 250);
  }

  private createCoverImageErrorPlaceholder(contentNode: Element): void {
    const localization = new LocalizationService().strings;
    const container = document.createElement('div');
    container.className = styles.IA_coverImageError;
    const title = document.createElement('div');
    title.className = styles.IA_coverImageErrorTitle;
    title.textContent = localization.Messages_ViewPanel_CoverImageOutdatedTitle;
    const message = document.createElement('div');
    message.className = styles.IA_coverImageErrorMessage;
    message.textContent = localization.Messages_ViewPanel_CoverImageOutdated;
    container.appendChild(title);
    container.appendChild(message);
    contentNode.firstChild.replaceWith(container);
    // Has to be set here to override the inline styling
    const el = contentNode as HTMLElement;
    el.style.height = 'auto';
    el.style.minHeight = 'unset';
    el.style.display = 'flex';
    el.style.justifyContent = 'center';
    el.style.alignItems = 'center';
  }

  private getCoverImageUrl(): string {
    return Helper.getImageUrl(
      Environment.getEnvironmentForImageAPI(this.props.messagesStore.environment),
      this.props.messagesStore.tenant,
      this.props.messagesStore.token,
      this.props.messagesStore.messageToOpen?.image,
      "1.0", "Full",
      this.props.messagesStore.messageToOpen?.iaConfiguration?.cropSettings,
      false, true
    );
  }

  private async handleCoverImage(contentNode: Element, elements: { element: Element, addMargin: boolean }[]): Promise<void> {
    if (contentNode.className?.indexOf("IA_coverPreviewContainer") === -1) {
      return;
    }

    if (contentNode.children[0].tagName === "IMG") {
      const coverImage = contentNode.firstChild as HTMLImageElement;
      coverImage.src = this.getCoverImageUrl();
      await new Promise<void>((resolve) => {
        coverImage.onload = () => resolve();
        coverImage.onerror = () => {
          this.createCoverImageErrorPlaceholder(contentNode);
          resolve();
        };
      });
      elements.unshift({ element: contentNode, addMargin: false });

    } else if (this.props.messagesStore.messageToOpen?.image) {
      //if cover is video and img is set - insert img with video thumbnail for the pdf
      const img = document.createElement('img');
      img.style.cssText = 'width:100%;height:100%;';
      img.id = "coverImageToReplace";
      img.src = this.getCoverImageUrl();
      const loaded = await new Promise<boolean>((resolve) => {
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
      });
      if (loaded) {
        this.setState({ coverVideoElement: contentNode.firstElementChild });
        contentNode.firstElementChild.replaceWith(img);
      } else {
        this.createCoverImageErrorPlaceholder(contentNode);
      }
      elements.unshift({ element: contentNode, addMargin: false });
    }
  }

  private async printMessage(): Promise<void> {
    this.setState({ printing: true, printProgressPercentage: 0 }, () => {
      setTimeout(async () => {
        // timeout is needed to make sure the "creating pdf"-screen is displayed before starting the process

        const panel: HTMLElement = document.getElementById(`slimPanelContentArea-${this.panelId}`);
        // gather elements that needs to be printed
        const elements: { element: Element, addMargin: boolean }[] = [];
        const panelChildren = Array.from(panel.children);

        // find all elements that should be printed
        for (const node of panelChildren) {
          if (node.className?.indexOf("content") !== -1) {
            const contentChildren = Array.from(node.children[0].children);
            for (const contentNode of contentChildren) {
              await this.handleCoverImage(contentNode, elements);
              if (contentNode.className?.indexOf("IA_viewPanelContent") !== -1) {
                const viewPanelContentChildren = Array.from(contentNode.children);
                viewPanelContentChildren.forEach(element => {
                  if (element.className?.indexOf("IA_reader") !== -1 && element.children.length > 0) {
                    let textNodes: Element[] = Array.from(element.children[0].children);
                    let addBreakAbove = false;
                    textNodes.forEach(text => {
                      if (text.firstChild?.nodeName === "BR") {
                        addBreakAbove = true;
                      } else {
                        elements.push({ element: text, addMargin: addBreakAbove });
                        addBreakAbove = false;
                      }
                    });
                  } else {
                    elements.push({ element: element, addMargin: false });
                  }
                });
              }
            }
          }
        }
        const documentSettings: jsPDFOptions = {
          format: "a4",
          unit: "px",
          hotfixes: ["px_scaling"]
        };
        this.buildPDFRecursively(elements, elements.length, new jsPDF(documentSettings), 0, []);
      }, 150);
    });
  }

  private buildPDFRecursively(elements: { element: Element, addMargin: boolean }[], elementCount: number, pdf: jsPDF, pagePosition: number, styleList: { element: HTMLElement, top: string, bottom: string, font: string }[]) {
    if (elements.length === 0) {
      this.setState({ printProgressPercentage: 100 }, () => {
        setTimeout(() => {
          // wait for animation to finish
          this.openPdf(pdf, styleList);
        }, 500);
      });
      return;
    }

    this.setState({
      printProgressPercentage: (1 - (elements.length / elementCount)) * 100
    })

    // Remove unloaded images from the entire document after the rerender so html2canvas never fetches them.
    document.querySelectorAll('img').forEach((img: HTMLImageElement) => {
      if (img.src && img.naturalWidth === 0) {
        img.removeAttribute('src');
      }
    });

    console.log(`building PDF: ${elements.length} elements left`);

    const html2canvas: any = (_html2canvas as any).default ?? _html2canvas;

    const pagePadding = 50;
    const elementMargin = 5;
    const lineBreakMargin = 10;
    const pdfHeight = pdf.internal.pageSize.getHeight() - pagePadding - pagePadding;
    const elementObject = elements.shift() as { element: HTMLElement, addMargin: boolean };
    const element = elementObject.element;
    const prevMarginTop = element.style.marginTop;
    const prevMarginBottom = element.style.marginBottom;
    const prevFontFamily = element.style.fontFamily;

    styleList.push({
      element: element,
      top: prevMarginTop,
      bottom: prevMarginBottom,
      font: prevFontFamily
    });

    // we have to remove the margin due to a bug in html2canvas when using firefox
    element.style.marginTop = "0px";
    element.style.marginBottom = "0px";

    // adding font family to make sure it is printed correctly
    element.style.fontFamily = "Segoe UI, sans-serif";
    element.setAttribute("lang", "");

    // we have to set the color of the SPAN child to black
    const spanChildren = element.querySelectorAll("span");
    spanChildren.forEach(span => {
      span.setAttribute("style", (span.getAttribute("style") ?? "") + "color: black;");
    });

    html2canvas(element, {
      allowTaint: false,
      useCORS: true,
      imageTimeout: 3000,
      scale: ViewPanel.PRINT_SCALE
    }).then(canvas => {
      const canvasData = canvas.toDataURL('image/png');
      if (pagePosition + element.clientHeight > pdfHeight && pagePosition !== 0) {
        // check if the next element will be too big to fit current page
        // add a new page and reset position
        pdf.addPage();
        pagePosition = 0;
      }

      const width = element.clientWidth !== 0 ? element.clientWidth : 10;
      const height = element.clientHeight !== 0 ? element.clientHeight : 10;
      const isFirstElement = pagePosition === 0;
      let margin = isFirstElement ? pagePadding : elementMargin;
      if (elementObject.addMargin) {
        margin += lineBreakMargin;
      }

      pdf.addImage(canvasData, 'PNG', pagePadding, pagePosition + margin, width, height);

      pagePosition += height + margin + elementMargin;

      return this.buildPDFRecursively(elements, elementCount, pdf, pagePosition, styleList);
    }).catch(error => {
      console.error("Element was not added to PDF", error, element);
      return this.buildPDFRecursively(elements, elementCount, pdf, pagePosition, styleList);
    });
  }

  private openPdf(pdf: jsPDF, styleList: { element: HTMLElement, top: string, bottom: string, font: string }[]): void {
    console.log("getting ready to open pdf...");

    styleList.forEach(e => {
      // re-add the correct margins to all elements
      e.element.style.marginTop = e.top;
      e.element.style.marginBottom = e.bottom;
      e.element.style.fontFamily = e.font;

      // remove the black font color from the span children
      const spanChildren = e.element.querySelectorAll("span");
      spanChildren.forEach(span => {
        span.setAttribute("style", (span.getAttribute("style") ?? "").replace("color: black;", ""));
      });
    });

    //when pdf has been created - replace with img with video again - to fix the view
    if (this.state.coverVideoElement) {

      var imageToReplace = document.querySelector("#coverImageToReplace");

      if (imageToReplace) {

        imageToReplace.replaceWith(this.state.coverVideoElement);
      }

    }

    // open pdf in new window
    const newTab = window.open(pdf.output('bloburl').toString(), '_blank');
    if (newTab) {
      newTab.addEventListener('load', () => {
        // not working on firefox
        // https://bugzilla.mozilla.org/show_bug.cgi?id=1251028
        newTab.print();
      });
    }
    this.setState({ printing: false });
  }

  private onOpenIAMessage(instance: string, messageId: string) {
    this.props.messagesStore.panelContentIsLoading = true;
    MessageService.getMessage(this.props.messagesStore.environment, this.props.messagesStore.token, this.props.messagesStore.tenant, instance, parseInt(messageId)).then(result => {
      if (result.messages[0]) {
        this.props.messagesStore.messagesOpenedStack.push(this.props.messagesStore.messageToOpen);
        this.props.messagesStore.messageToOpen = result.messages[0];
        document.getElementById(`slimPanelContentArea-${this.panelId}`)?.scrollTo(0, 0);
      } else {
        this.props.messagesStore.showNotification(
          "error",
          this.localizationService.strings.Messages_ViewPanel_MessageNotFound,
          "Attention"
        );
      }
    }).catch(() => {
      this.props.messagesStore.showNotification(
        "error",
        this.localizationService.strings.Messages_ViewPanel_ErrorWhenFetchingMessage,
        "Attention"
      );
    }).finally(() => {
      this.props.messagesStore.panelContentIsLoading = false;
    });
  }

  public render(): JSX.Element {
    let date: string;
    if (this.props.messagesStore.messageToOpen != undefined && this.props.messagesStore.messageToOpen.startDate != undefined) {
      date = this.dateTimeService.ConvertToDDMMYYYY(this.props.messagesStore.messageToOpen.startDate) === this.dateTimeService.ConvertToDDMMYYYY(new Date()) ? this.localizationService.strings.Messages_ViewPanel_Today + " " + this.dateTimeService.ConvertToHHMM(this.props.messagesStore.messageToOpen.startDate) : this.dateTimeService.ConvertToDDMMYYYYHHMM(this.props.messagesStore.messageToOpen.startDate);
    }
    let top = 0;
    if (Helper.getDisplayEnvironment() === "Sharepoint" || Helper.getDisplayEnvironment() === "SharepointClassic") {
      top = 48;
    }
    if (this.props.messagesStore.isMobileApp) {
      top = window.platform === "android" ? 50 : Helper.isIPhoneXType() ? 100 : 80;
    }
    let videoUrl = this.props.messagesStore.messageToOpen?.videoUrl;
    if ((location.href.includes("sharepoint.com") && this.props.messagesStore.messageToOpen?.videoEmbedCode) || (videoUrl == undefined && this.props.messagesStore.messageToOpen?.videoEmbedCode)) {
      videoUrl = Helper.getVideoSrcFromEmbedCode(this.props.messagesStore.messageToOpen?.videoEmbedCode);
    }
    return (
      <IAPanel
        skeletonLoaderElements={Helper.getMessagesSkeletonElements(false, this.props.messagesStore.isMobile)}
        isFullPageView={this.props.messagesStore.isFullPageView && !this.props.messagesStore.isMobile}
        showUniqueViews={this.props.messagesStore.showUniqueViews}
        uniqueViewsCount={this.props.messagesStore.messageToOpen?.uniqueViews}
        showNavigationBar={true}
        iaConfiguration={this.props.messagesStore.messageToOpen?.iaConfiguration}
        date={date}
        isMobile={this.props.messagesStore.isMobile}
        open={this.props.messagesStore.messageToOpen != undefined && this.props.id != undefined}
        transition={this.props.openingFromLink ? "none" : this.props.messagesStore.panelIsOpen ? "fade" : "slide"}
        width={this.props.messagesStore.isMobile ? window.innerWidth : (this.props.messagesStore.isFullPageView ? window.innerWidth : 700)}
        highlightColor={this.props.messagesStore.highlightColor}
        marginTop={this.props.top ? this.props.top : top}
        likesEnabled={this.props.messagesStore.likesEnabled && !this.props.messagesStore.messageToOpen?.iaConfiguration?.disableLikes && !this.state.printing}
        commentsEnabled={this.props.messagesStore.commentsEnabled && !this.props.messagesStore.messageToOpen?.iaConfiguration?.disableComments && !this.state.printing}
        likesOnCommentsEnabled={this.props.messagesStore.commentsEnabled}
        user={this.props.messagesStore.user}
        itemId={this.props.messagesStore.messageToOpen?.id?.toString()}
        tenant={this.props.messagesStore.tenant}
        component={this.props.messagesStore.component}
        instance={this.props.messagesStore.instance}
        token={this.props.messagesStore.token}
        environment={this.props.messagesStore.environment}
        source={this.props.messagesStore.source}
        mobileCloseButton={true}
        imageViewerImages={this.state.imageViewerImages}
        imageViewerIndex={this.state.imageViewerIndex}
        webpartId={this.props.messagesStore.webpartId}
        closeImageViewer={() => {
          this.setState({ imageViewerImages: undefined, imageViewerIndex: undefined });
        }}
        close={() => {
          if (this.props.messagesStore && this.props.messagesStore.messageToOpen !== undefined) {
            if (this.props.isContentCreator) {
              this.props.onClose();
              // Fix for mobile browser z-index issue
              if (this.props.messagesStore.isMobile && document.getElementById("IA_GlobalNavigator")) {
                const navigatorElement = document.getElementById("IA_GlobalNavigator");
                if (navigatorElement) {
                  navigatorElement.style.display = "block";
                }
              }
              //
            } else {
              this.props.messagesStore.clearTranslation();
              if (this.props.messagesStore.messagesOpenedStack.length > 0) {
                // This is triggered if a message is opened from another message - in this case we open the previous message
                this.openPreviouslyOpenedMessage();
              } else {
                this.props.messagesStore.closeMessage();
                if (this.props.onClose) {
                  this.props.onClose();
                  // Fix for mobile browser z-index issue
                  if (this.props.messagesStore.isMobile && document.getElementById("IA_GlobalNavigator")) {
                    const navigatorElement = document.getElementById("IA_GlobalNavigator");
                    if (navigatorElement) {
                      navigatorElement.style.display = "block";
                    }
                  }
                  //
                }
              }
            }
          }
        }}
        onEdit={this.props.isContentCreator ?
          () => {
            this.props.onEdit()
          }
          :
          ConditionalOutputFactory
            .WithDefault<() => void>(undefined)
            .DoNotFilterEmptyValues()
            .OnCondition(this.props.messagesStore.userRole == "Administrator", () => this.onIAPanelEditClicked())
            .OnCondition(this.props.messagesStore.userRole == "Editor"
              && this.props.messagesStore.messageToOpen?.author?.email?.toLowerCase() == this.props.messagesStore.user?.email?.toLowerCase(), () => this.onIAPanelEditClicked())
            .OnCondition(this.props.messagesStore.userRole == "Editor"
              && this.props.messagesStore.messageToOpen?.editor?.email?.toLowerCase() == this.props.messagesStore.user?.email?.toLowerCase(), () => this.onIAPanelEditClicked())
            .OnCondition(this.props.messagesStore.messageToOpen?.external, undefined)
            .Last()
        }
        enableNext={this.enableNext()}
        enablePrevious={this.enablePrevious()}
        loading={this.props.messagesStore.panelContentIsLoading || this.props.messagesStore.loading}
        previousMessage={this.props.messagesStore.openedFromLink ? undefined : () => this.previousMessage()}
        nextMessage={this.props.messagesStore.openedFromLink ? undefined : () => this.nextMessage()}
        likes={this.props.messagesStore.messageToOpen != undefined ? this.props.messagesStore.messageToOpen.likes : undefined}
        commentCount={this.props.messagesStore.messageToOpen != undefined ? this.props.messagesStore.messageToOpen.commentCount : undefined}
        commentOpenedFromLink={this.props.messagesStore.openContentFromLink?.commentToShow ?? undefined}
        showEmojiPickerExpandButton={this.props.messagesStore.instance?.settings?.showEmojiPickerExpandButton}
        reactionOptions={this.props.messagesStore.instance?.settings?.reactionOptions}
        likesUpdated={(likes: any[]) => {
          this.props.messagesStore.likesUpdated(likes);
          this.forceUpdate();
        }}
        commentsUpdated={(comments: number) => {
          this.props.messagesStore.commentsUpdated(comments);
          this.forceUpdate();
        }}
        addComment={this.props.messagesStore.addComment}
        commentClicked={
          () => {
            this.props.messagesStore.updateEditorState();
          }
        }
        onCopyLink={
          (this.props.messagesStore.component === "Messages" && this.props.messagesStore.instance?.settings?.openMessageLink) ?
            () => {
              // this.props.messagesStore.showNotification(
              //   "success",
              //   "The link has been copied to the clipboard!",
              //   "Emoji"
              // );
            } : undefined
        }
        commentCanceled={
          () => {
            this.props.messagesStore.updateEditorState();
          }
        }
        onEmailLink={(this.props.messagesStore.component === "Messages" && this.props.messagesStore.instance?.settings?.openMessageLink) ? () => this.emailLink() : undefined}
        onExportToCalender={this.props.messagesStore.component === "Event" && this.props.messagesStore.messageToOpen && !this.props.messagesStore.messageToOpen?.external ? `${Environment.getEnvironmentForEventsAPI(this.props.messagesStore.environment)}/${this.props.messagesStore.tenant.replace('.sharepoint.com', '')}/${this.props.messagesStore.instance?.instanceName}/event/${this.props.messagesStore.messageToOpen.id}/export?token=${this.props.messagesStore.token}` : undefined}
        onTranslate={(language: string, itemId: string) => {
          this.props.messagesStore.isTranslating = true;
          this.setState({
            translateToLanguage: {
              language,
              itemId
            }
          });
        }}
        enableTranslation={this.props.messagesStore.instance?.translationEnabled && !this.props.messagesStore.messageToOpen?.external}
        isTranslating={this.props.messagesStore.isTranslating}
        dataAutomationIdPrefix={this.props.dataAutomationIdPrefix}
        enablePrint={this.props.enablePrint ?? (this.props.messagesStore.instance?.settings && this.props.messagesStore.instance?.settings.enablePrinting && !this.props.messagesStore.messageToOpen?.external)}
        panelId={this.panelId}
        printing={this.state.printing}
        printProgressPercentage={this.state.printProgressPercentage}
        printMessage={() => this.printMessage()}
        isContentCreator={this.props.isContentCreator}
        userRole={this.props.messagesStore.userRole}
        onCommentOpenIAMessage={(instance, messageId) => this.onOpenIAMessage(instance, messageId)}
        onCommentContentImageClicked={(imageUrl) => {
          this.setState({ imageViewerIndex: 0 });
          this.setState({
            imageViewerImages: [{
              url: imageUrl
            }]
          });
        }}
        openedFromAnotherMessage={this.props.messagesStore.messagesOpenedStack[this.props.messagesStore.messagesOpenedStack.length - 1]}
      >
        {this.state.showRecurrenceEditPopup &&
          <ViewPanelRecurrenceEditPopup
            onEditCallback={(isOccurrence) => {
              this.props.messagesStore.editOccurrence = isOccurrence;
              const messageToOpen = this.props.messagesStore.messageToOpen;
              setTimeout(() => {
                if (!this.props.messagesStore.messageToOpen) {
                  this.setState({ showRecurrenceEditPopup: false });
                  return;
                }

                const messageToEdit = { ...messageToOpen };

                if (isOccurrence) {
                  this.props.messagesStore.occurrenceSeriesId = messageToOpen.id;
                  messageToEdit.recurrence = false;
                  messageToEdit.recurrenceData = undefined;
                  messageToEdit.id = undefined;
                  this.props.messagesStore.creatingNewMessage = true;
                }

                this.props.messagesStore.editMessage(messageToEdit);
                this.setState({ showRecurrenceEditPopup: false });
              }, 200);
            }}
            onRemovePopupCallback={() => this.setState({ showRecurrenceEditPopup: false })}
            highlightColor={this.props.messagesStore.highlightColor}
          />
        }
        {this.props.messagesStore.messageToOpen && this.props.messagesStore.messageToOpen.iaConfiguration && this.props.messagesStore.messageToOpen.iaConfiguration.composerComponents &&
          <IAViewer
            token={this.props.messagesStore.token}
            tenant={this.props.messagesStore.tenant}
            environment={this.props.messagesStore.environment}
            isMobile={this.props.messagesStore.isMobile}
            fontSlots={this.props.messagesStore.fontSlots}
            highlightColor={this.props.messagesStore.highlightColor}
            component={this.props.messagesStore.component}
            instance={this.props.messagesStore.instance}
            id={`${this.props.id}`}
            isAdmin={this.props.messagesStore.isAdminPage}
            imageUrl={this.props.messagesStore.messageToOpen.image}
            imageCropSettings={this.props.messagesStore.messageToOpen?.iaConfiguration?.cropSettings}
            onlyShowCoverImageInRollup={this.props.messagesStore.messageToOpen.iaConfiguration.onlyShowCoverImageInRollup}
            videoUrl={videoUrl}
            title={this.props.messagesStore.messageToOpen.title}
            teaser={this.props.messagesStore.messageToOpen.teaser}
            composerComponents={this.props.messagesStore.messageToOpen.iaConfiguration.composerComponents}
            categories={this.props.messagesStore.instance?.settings?.hideTagsInPanel ? undefined : this.props.messagesStore.messageToOpen.categories}
            startDate={this.props.messagesStore.messageToOpen.startDate}
            endDate={this.props.messagesStore.messageToOpen.endDate}
            allDayEvent={this.props.messagesStore.messageToOpen.allDayEvent}
            location={this.props.messagesStore.messageToOpen.location}
            modified={this.props.messagesStore.messageToOpen && this.props.messagesStore.messageToOpen.modified ? this.props.messagesStore.messageToOpen.modified : new Date()}
            author={this.props.messagesStore.messageToOpen.author}
            contacts={this.props.messagesStore.messageToOpen.contacts}
            editor={this.props.messagesStore.messageToOpen.editor}
            attachments={this.props.messagesStore.messageToOpen.iaConfiguration.attachments && this.props.messagesStore.messageToOpen.iaConfiguration.attachments ? this.props.messagesStore.messageToOpen.iaConfiguration.attachments : undefined}
            likesEnabled={this.props.messagesStore.likesEnabled && !this.props.messagesStore.messageToOpen.iaConfiguration.disableLikes}
            commentsEnabled={this.props.messagesStore.commentsEnabled && !this.props.messagesStore.messageToOpen.iaConfiguration.disableComments}
            likesOnCommentsEnabled={this.props.messagesStore.commentsEnabled}
            translateToLanguage={this.state.translateToLanguage}
            doneTranslating={() => {
              this.props.messagesStore.isTranslating = false;
            }}
            imageViewerImagesToShow={(images: IComposerImage[]) => {
              this.setState({ imageViewerImages: images });
            }}
            imageViewerIndexToShow={(index: number) => {
              this.setState({ imageViewerIndex: index });
            }}
            isFullPage={this.props.messagesStore.isFullPageView}
            iaMessageBgImagesFromBlob={this.props.messagesStore.messageToOpen.iaMessageBgImagesFromBlob}
            onOpenIAMessage={(instance, messageId) => this.onOpenIAMessage(instance, messageId)}
            newsLink={this.props.messagesStore.messageToOpen?.iaConfiguration?.newsLink}
            openNewsLinkInNewTab={this.props.messagesStore.messageToOpen?.iaConfiguration?.openNewsLinkInNewTab}
            padding={this.props.messagesStore.isMobile ? "0 30px" : "0 40px"}
            showAllButtonLink={this.props.messagesStore.showAllButtonLink}
            fieldsToHide={this.props.messagesStore.messageToOpen?.iaConfiguration?.fieldsToHide}
            isPrinting={this.state.printing}
          />
        }
      </IAPanel >
    );
  }

  private onIAPanelEditClicked() {
    if (this.props.messagesStore.messageToOpen?.recurrence && this.props.messagesStore.component === "Event") {
      this.setState({ showRecurrenceEditPopup: true });
    } else {
      this.props.messagesStore.editMessage(this.props.messagesStore.messageToOpen);
    }
  }
}